import React, { useEffect, useState, Component } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Badge } from '../ui/Badge';
import { DatePicker } from '../ui/DatePicker';
import { Tooltip } from '../ui/Tooltip';
import { Plus, User, Trash2, Calendar as CalendarIcon, Clock, Save, ChevronRight, ChevronLeft, ClipboardList, Settings2, Info, CheckCircle2, FileText, Mail, Download, Upload, Image as ImageIcon, AlertTriangle, Lightbulb, Edit, X, Pause, Play, ChevronDown, Search } from 'lucide-react';
import { operativeActivitiesStore, customStepOptions, standardStepTitles, OperativeActivity, getStepTitlesForActivity } from '../../services/operativeActivities';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../../contexts/NotificationContext';
import { SearchableSelect } from '../ui/SearchableSelect';
import { mockData } from '../../services/mockData';
// --- Types ---
interface Action {
  id: string;
  name: string;
  dateTime: string;
  assignee: string;
  deadline: string;
  notes: string;
  isDone: boolean;
}
interface ExtraActivity {
  id: string;
  type: string;
  dateTime: string;
  user: string;
  notes: string;
}
interface NC {
  id: string;
  number: string;
  type: string;
  origin: string;
  description: string;
  date: string;
  responsible: string;
}
interface Improvement {
  id: string;
  number: string;
  subject: string;
  description: string;
  date: string;
  responsible: string;
}
interface IntegrationItem {
  checked: boolean;
  text: string;
  dateTime: string;
  user: string;
  extraDate?: string; // For Calendarizzazione consegna
}
interface StepData {
  consultantActions: Action[];
  clientActions: Action[];
  extraActivities: ExtraActivity[];
  nonConformities: NC[];
  improvements: Improvement[];
  annotations: string;
  images: string[];
  integrations: Record<string, IntegrationItem>;
  // For step 5+ (Attività integrativa) and personalizzata steps
  integrativeDesc?: string;
  integrativeType?: string;
  integrativeCustomName?: string;
  integrativeDate?: string; // Mandatory date for personalizzata steps
  // Tempo attività (cumulative time tracking for steps 2-5)
  timeEntries?: {
    hours: number;
    minutes: number;
  }[];
  totalTimeHours?: number;
  totalTimeMinutes?: number;
}
interface GenerateActivityWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractNumber?: string;
  clientName?: string;
  editActivity?: OperativeActivity;
}
// --- Constants ---
const adminUsers = [{
  value: 'Alessio',
  label: 'Alessio'
}, {
  value: 'Lorenzo',
  label: 'Lorenzo'
}, {
  value: 'Gian Franco',
  label: 'Gian Franco'
}, {
  value: 'Tiziano',
  label: 'Tiziano'
}];
const extraActivityTypes = [{
  value: 'Telefonata',
  label: 'Telefonata'
}, {
  value: 'Email',
  label: 'Email'
}, {
  value: 'Sopralluogo',
  label: 'Sopralluogo'
}, {
  value: 'Videochiamata',
  label: 'Videochiamata'
}, {
  value: 'Richiesta aggiuntiva',
  label: 'Richiesta aggiuntiva'
}, {
  value: 'Sollecito',
  label: 'Sollecito'
}];
const productsList = ['Consulenza Sicurezza', 'HACCP Base', 'Privacy GDPR', 'DVR', 'Formazione Lavoratori', 'Consulenza Ambiente', 'Antiriciclaggio'];
const getCurrentDateTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};
// --- Main Component ---
export function GenerateActivityWizardModal({
  isOpen,
  onClose,
  contractNumber,
  clientName,
  editActivity
}: GenerateActivityWizardModalProps) {
  const {
    addNotification
  } = useNotifications();
  // --- State: Flow & Config ---
  const [step, setStep] = useState(0); // 0: Type, 1: Config, 2+: Steps
  const [activityMode, setActivityMode] = useState<'standard' | 'personalizzata'>('standard');
  const [product, setProduct] = useState('');
  const [isRepeatable, setIsRepeatable] = useState(false);
  const [numRepetitions, setNumRepetitions] = useState(2);
  const [selectedContract, setSelectedContract] = useState<string>('');
  const [selectedClientName, setSelectedClientName] = useState<string>('');
  const [activityStatus, setActivityStatus] = useState<'Attiva' | 'Sospesa'>('Attiva');
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Dynamic step titles (starts with standard 5, can grow)
  const [dynamicStepTitles, setDynamicStepTitles] = useState<string[]>([...standardStepTitles]);
  // --- State: Data ---
  const [stepsData, setStepsData] = useState<Record<number, StepData>>({});
  // Helper to create empty step data
  const createEmptyStepData = (): StepData => ({
    consultantActions: [],
    clientActions: [],
    extraActivities: [],
    nonConformities: [],
    improvements: [],
    annotations: '',
    images: [],
    integrations: {},
    timeEntries: [],
    totalTimeHours: 0,
    totalTimeMinutes: 0
  });
  // Initialize step data when moving from type choice to config
  const initializeStepsForMode = (mode: 'standard' | 'personalizzata') => {
    if (mode === 'standard') {
      const initialData: Record<number, StepData> = {};
      for (let i = 2; i <= 6; i++) {
        initialData[i] = createEmptyStepData();
      }
      setStepsData(initialData);
      setDynamicStepTitles([...standardStepTitles]);
    } else {
      // Personalizzata: start with 1 custom step
      setStepsData({
        2: createEmptyStepData()
      });
      setDynamicStepTitles(['Attività personalizzata 1']);
    }
  };
  // Reset on open
  useEffect(() => {
    if (isOpen) {
      if (editActivity) {
        setActivityMode(editActivity.activityType);
        setProduct(editActivity.product);
        const titles = getStepTitlesForActivity(editActivity);
        setDynamicStepTitles(titles);
        const initialData: Record<number, StepData> = {};
        for (let i = 2; i <= titles.length + 1; i++) {
          initialData[i] = createEmptyStepData();
        }
        setStepsData(initialData);
        setStep(2);
      } else {
        setStep(0);
        setStepsData({});
        setDynamicStepTitles([...standardStepTitles]);
        setActivityStatus('Attiva');
        setProduct('');
        setIsRepeatable(false);
        setNumRepetitions(2);
        setSelectedContract(contractNumber || '');
        setSelectedClientName(clientName || '');
      }
    }
  }, [isOpen, editActivity, contractNumber, clientName]);
  // --- State: UI Toggles & Forms ---
  const [showAddConsultantAction, setShowAddConsultantAction] = useState(false);
  const [showAddClientAction, setShowAddClientAction] = useState(false);
  const [showAddExtraActivity, setShowAddExtraActivity] = useState(false);
  const [consultantActionForm, setConsultantActionForm] = useState<Partial<Action>>({});
  const [clientActionForm, setClientActionForm] = useState<Partial<Action>>({});
  const [extraActivityForm, setExtraActivityForm] = useState<Partial<ExtraActivity>>({});
  // Report Dialogs
  const [showReportContentDialog, setShowReportContentDialog] = useState(false);
  const [reportActionType, setReportActionType] = useState<'pdf' | 'word' | 'email' | null>(null);
  const [reportIncludeConsultant, setReportIncludeConsultant] = useState(true);
  const [reportIncludeClient, setReportIncludeClient] = useState(true);
  // Email Dialog
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: 'email@cliente.it',
    subject: '',
    body: ''
  });
  // NC/Improvement Modals
  const [showNCModal, setShowNCModal] = useState(false);
  const [showImpModal, setShowImpModal] = useState(false);
  const [ncForm, setNcForm] = useState<Partial<NC>>({});
  const [impForm, setImpForm] = useState<Partial<Improvement>>({});
  // --- Handlers: Navigation ---
  const handleNextStep = () => {
    if (step === 0) {
      initializeStepsForMode(activityMode);
      setStep(1);
    } else if (step === 1) {
      // Auto-assign date for personalizzata step 2
      if (activityMode === 'personalizzata') {
        setStepsData(prev => ({
          ...prev,
          [2]: {
            ...prev[2],
            integrativeDate: prev[2]?.integrativeDate || getCurrentDateTime()
          }
        }));
      }
      setStep(2);
    } else if (step < dynamicStepTitles.length + 1) {
      const nextStep = step + 1;
      // Auto-assign date for personalizzata steps
      if (activityMode === 'personalizzata') {
        setStepsData(prev => ({
          ...prev,
          [nextStep]: {
            ...prev[nextStep],
            integrativeDate: prev[nextStep]?.integrativeDate || getCurrentDateTime()
          }
        }));
      }
      setStep(nextStep);
      resetForms();
    } else {
      // Save
      if (editActivity) {
        operativeActivitiesStore.updateActivity(editActivity.id, {
          product: product,
          customStepNames: activityMode === 'personalizzata' ? dynamicStepTitles : undefined
        });
      } else {
        operativeActivitiesStore.addActivity({
          activityType: activityMode,
          contractNumber: selectedContract || 'N/D',
          clientName: selectedClientName || 'N/D',
          operator: 'Alessio',
          deadline: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          title: selectedClientName ? `Attività ${activityMode} — ${selectedClientName}` : `Attività ${activityMode}`,
          product: product,
          customStepNames: activityMode === 'personalizzata' ? dynamicStepTitles : undefined
        });
      }
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        handleFullReset();
        onClose();
      }, 1800);
    }
  };
  const handlePrevStep = () => {
    if (step > 0) {
      setStep(step - 1);
      resetForms();
    }
  };
  const resetForms = () => {
    setShowAddConsultantAction(false);
    setShowAddClientAction(false);
    setShowAddExtraActivity(false);
    setConsultantActionForm({});
    setClientActionForm({});
    setExtraActivityForm({});
  };
  const handleFullReset = () => {
    setStep(0);
    setActivityMode('standard');
    setProduct('');
    setIsRepeatable(false);
    setNumRepetitions(2);
    setSaveSuccess(false);
    setStepsData({});
    setDynamicStepTitles([...standardStepTitles]);
    setActivityStatus('Attiva');
    setSelectedContract('');
    setSelectedClientName('');
    resetForms();
  };
  const handleClose = () => {
    onClose();
    setTimeout(handleFullReset, 300);
  };
  // --- Handlers: Actions ---
  const handleAddConsultantAction = () => {
    if (!consultantActionForm.name) return;
    const newAction: Action = {
      id: Math.random().toString(36).substr(2, 9),
      name: consultantActionForm.name || '',
      dateTime: consultantActionForm.dateTime || getCurrentDateTime(),
      assignee: consultantActionForm.assignee || 'io',
      deadline: consultantActionForm.deadline || '',
      notes: consultantActionForm.notes || '',
      isDone: false
    };
    setStepsData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        consultantActions: [...prev[step].consultantActions, newAction]
      }
    }));
    setShowAddConsultantAction(false);
    setConsultantActionForm({});
  };
  const handleAddClientAction = () => {
    if (!clientActionForm.name) return;
    const newAction: Action = {
      id: Math.random().toString(36).substr(2, 9),
      name: clientActionForm.name || '',
      dateTime: clientActionForm.dateTime || getCurrentDateTime(),
      assignee: clientActionForm.assignee || '',
      deadline: clientActionForm.deadline || '',
      notes: clientActionForm.notes || '',
      isDone: false
    };
    setStepsData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        clientActions: [...prev[step].clientActions, newAction]
      }
    }));
    setShowAddClientAction(false);
    setClientActionForm({});
  };
  const handleAddExtraActivity = () => {
    if (!extraActivityForm.type) return;
    const newExtra: ExtraActivity = {
      id: Math.random().toString(36).substr(2, 9),
      type: extraActivityForm.type || '',
      dateTime: extraActivityForm.dateTime || getCurrentDateTime(),
      user: extraActivityForm.user || 'io',
      notes: extraActivityForm.notes || ''
    };
    setStepsData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        extraActivities: [...prev[step].extraActivities, newExtra]
      }
    }));
    setShowAddExtraActivity(false);
    setExtraActivityForm({});
  };
  const toggleActionDone = (type: 'consultant' | 'client', id: string) => {
    setStepsData(prev => {
      const listKey = type === 'consultant' ? 'consultantActions' : 'clientActions';
      const list = prev[step][listKey];
      const updatedList = list.map(a => a.id === id ? {
        ...a,
        isDone: !a.isDone
      } : a);
      return {
        ...prev,
        [step]: {
          ...prev[step],
          [listKey]: updatedList
        }
      };
    });
  };
  const deleteAction = (type: 'consultant' | 'client', id: string) => {
    setStepsData(prev => {
      const listKey = type === 'consultant' ? 'consultantActions' : 'clientActions';
      return {
        ...prev,
        [step]: {
          ...prev[step],
          [listKey]: prev[step][listKey].filter(a => a.id !== id)
        }
      };
    });
  };
  const deleteExtraActivity = (id: string) => {
    setStepsData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        extraActivities: prev[step].extraActivities.filter(a => a.id !== id)
      }
    }));
  };
  // --- Handlers: Integrations ---
  const handleIntegrationChange = (key: string, field: keyof IntegrationItem, value: any) => {
    setStepsData(prev => {
      const current = prev[step].integrations[key] || {
        checked: false,
        text: '',
        dateTime: getCurrentDateTime(),
        user: 'io'
      };
      // If checking for the first time, set default datetime and user
      if (field === 'checked' && value === true && !current.checked) {
        return {
          ...prev,
          [step]: {
            ...prev[step],
            integrations: {
              ...prev[step].integrations,
              [key]: {
                ...current,
                checked: true,
                dateTime: getCurrentDateTime(),
                user: 'io'
              }
            }
          }
        };
      }
      return {
        ...prev,
        [step]: {
          ...prev[step],
          integrations: {
            ...prev[step].integrations,
            [key]: {
              ...current,
              [field]: value
            }
          }
        }
      };
    });
  };
  const handleAddIntegrativeStep = () => {
    const newStepNum = dynamicStepTitles.length + 2; // +2 because step 0 and 1 are config
    const newTitle = activityMode === 'personalizzata' ? `Attività personalizzata ${dynamicStepTitles.length + 1}` : 'Attività integrativa';
    setDynamicStepTitles([...dynamicStepTitles, newTitle]);
    setStepsData(prev => ({
      ...prev,
      [newStepNum]: createEmptyStepData()
    }));
    addNotification('success', 'Step aggiunto', `Nuovo step ${activityMode === 'personalizzata' ? 'personalizzato' : 'integrativo'} aggiunto al percorso.`);
  };
  // --- Handlers: Reports ---
  const handleReportClick = (type: 'pdf' | 'word' | 'email') => {
    setReportActionType(type);
    setShowReportContentDialog(true);
  };
  const confirmReportContent = () => {
    setShowReportContentDialog(false);
    if (reportActionType === 'email') {
      setEmailForm({
        to: 'email@cliente.it',
        subject: `Report attività ${product || ''}`,
        body: ''
      });
      setShowEmailDialog(true);
    } else {
      addNotification('success', 'Report generato', `Il report in formato ${reportActionType?.toUpperCase()} è stato generato con successo.`);
    }
  };
  const sendEmail = () => {
    setShowEmailDialog(false);
    addNotification('success', 'Email inviata', 'Il report è stato inviato con successo al cliente.');
  };
  // --- Handlers: NC / Improvements ---
  const handleSaveNC = () => {
    if (!ncForm.description) return;
    const newNC: NC = {
      id: Math.random().toString(36).substr(2, 9),
      number: `NC-2025/${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      type: ncForm.type || 'Non conformità minore',
      origin: ncForm.origin || 'Interna',
      description: ncForm.description,
      date: ncForm.date || new Date().toISOString().split('T')[0],
      responsible: ncForm.responsible || 'io'
    };
    setStepsData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        nonConformities: [...prev[step].nonConformities, newNC]
      }
    }));
    setShowNCModal(false);
    setNcForm({});
    addNotification('success', 'Non conformità salvata', 'La non conformità è stata aggiunta.');
  };
  const handleSaveImp = () => {
    if (!impForm.description) return;
    const newImp: Improvement = {
      id: Math.random().toString(36).substr(2, 9),
      number: `MIG-2025/${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      subject: impForm.subject || 'Nuovo miglioramento',
      description: impForm.description,
      date: impForm.date || new Date().toISOString().split('T')[0],
      responsible: impForm.responsible || 'io'
    };
    setStepsData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        improvements: [...prev[step].improvements, newImp]
      }
    }));
    setShowImpModal(false);
    setImpForm({});
    addNotification('success', 'Miglioramento salvato', 'Il miglioramento è stato aggiunto.');
  };
  // --- Render Helpers ---
  const renderActionList = (type: 'consultant' | 'client') => {
    const data = stepsData[step];
    if (!data) return null;
    const listKey = type === 'consultant' ? 'consultantActions' : 'clientActions';
    const actions = data[listKey];
    const activeActions = actions.filter(a => !a.isDone);
    const doneActions = actions.filter(a => a.isDone);
    return <div className="space-y-3 flex-1 mb-4" data-id="element-529">
        {/* Active Actions */}
        {activeActions.map(action => <div key={action.id} className="bg-slate-50 p-3 rounded-lg border border-slate-200 relative group flex gap-3" data-id="element-530">
            <div className="pt-0.5" data-id="element-531">
              <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-seform-orange focus:ring-seform-orange cursor-pointer" checked={action.isDone} onChange={() => toggleActionDone(type, action.id)} data-id="element-532" />
            </div>
            <div className="flex-1" data-id="element-533">
              <button onClick={() => deleteAction(type, action.id)} className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-534">
                <Trash2 className="h-4 w-4" data-id="element-535" />
              </button>
              <p className="font-semibold text-sm text-slate-800 pr-6" data-id="element-536">
                {action.name}
              </p>
              <div className="mt-2 space-y-1 text-xs text-slate-600" data-id="element-537">
                {action.dateTime && <p className="flex items-center gap-1" data-id="element-538">
                    <CalendarIcon className="h-3 w-3" data-id="element-539" /> Inizio:{' '}
                    {action.dateTime.replace('T', ' ')}
                  </p>}
                {action.assignee && <p className="flex items-center gap-1" data-id="element-540">
                    <User className="h-3 w-3" data-id="element-541" />{' '}
                    {action.assignee === 'io' ? 'Io' : action.assignee}
                  </p>}
                {action.deadline && <p className="flex items-center gap-1" data-id="element-542">
                    <Clock className="h-3 w-3" data-id="element-543" /> Scadenza:{' '}
                    {action.deadline.replace('T', ' ')}
                  </p>}
                {action.notes && <p className="mt-2 italic text-slate-500" data-id="element-544">{action.notes}</p>}
              </div>
            </div>
          </div>)}

        {/* Done Actions */}
        {doneActions.length > 0 && <div className="pt-2 mt-2 border-t border-slate-100" data-id="element-545">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2" data-id="element-546">
              Completate
            </p>
            {doneActions.map(action => <div key={action.id} className="bg-slate-50/50 p-3 rounded-lg border border-slate-100 relative flex gap-3 opacity-70" data-id="element-547">
                <div className="pt-0.5" data-id="element-548">
                  <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-seform-orange focus:ring-seform-orange cursor-pointer" checked={action.isDone} onChange={() => toggleActionDone(type, action.id)} data-id="element-549" />
                </div>
                <div className="flex-1" data-id="element-550">
                  <p className="font-semibold text-sm text-slate-500 line-through" data-id="element-551">
                    {action.name}
                  </p>
                </div>
              </div>)}
          </div>}

        {actions.length === 0 && !(type === 'consultant' ? showAddConsultantAction : showAddClientAction) && <p className="text-sm text-slate-400 italic text-center py-4" data-id="element-552">
              Nessuna azione inserita
            </p>}
      </div>;
  };
  const renderIntegrationRow = (key: string, label: string, isRadio = false, radioGroup = '') => {
    const data = stepsData[step]?.integrations[key] || {
      checked: false,
      text: '',
      dateTime: '',
      user: 'io'
    };
    const handleCheck = (checked: boolean) => {
      if (isRadio && checked) {
        // Uncheck others in group
        const currentIntegrations = {
          ...stepsData[step].integrations
        };
        Object.keys(currentIntegrations).forEach(k => {
          if (k.startsWith(radioGroup) && k !== key) {
            currentIntegrations[k] = {
              ...currentIntegrations[k],
              checked: false
            };
          }
        });
        currentIntegrations[key] = {
          ...data,
          checked: true,
          dateTime: data.dateTime || getCurrentDateTime(),
          user: data.user || 'io'
        };
        setStepsData(prev => ({
          ...prev,
          [step]: {
            ...prev[step],
            integrations: currentIntegrations
          }
        }));
      } else {
        handleIntegrationChange(key, 'checked', checked);
      }
    };
    return <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mb-3" data-id="element-553">
        <div className="flex items-center gap-3 mb-2" data-id="element-554">
          <input type={isRadio ? 'radio' : 'checkbox'} name={isRadio ? radioGroup : key} id={key} checked={data.checked} onChange={e => handleCheck(e.target.checked)} className={`w-4 h-4 text-seform-orange focus:ring-seform-orange border-slate-300 ${isRadio ? 'rounded-full' : 'rounded'}`} data-id="element-555" />
          <label htmlFor={key} className="font-semibold text-sm text-slate-800 cursor-pointer" data-id="element-556">
            {label}
          </label>
        </div>

        {data.checked && <div className="ml-7 grid grid-cols-1 md:grid-cols-12 gap-3 animate-in fade-in slide-in-from-top-1" data-id="element-557">
            <div className="md:col-span-6" data-id="element-558">
              <Input placeholder="Breve annotazione..." value={data.text} onChange={e => handleIntegrationChange(key, 'text', e.target.value)} data-id="element-559" />
            </div>
            <div className="md:col-span-3" data-id="element-560">
              <Input type="datetime-local" value={data.dateTime} onChange={e => handleIntegrationChange(key, 'dateTime', e.target.value)} data-id="element-561" />
            </div>
            <div className="md:col-span-3" data-id="element-562">
              <Select options={adminUsers} value={data.user} onChange={e => handleIntegrationChange(key, 'user', e.target.value)} data-id="element-563" />
            </div>
            {key === 'bo_calendarizzazione' && <div className="md:col-span-4 mt-2" data-id="element-564">
                <Input label="Data e ora della consegna" type="datetime-local" value={data.extraDate || ''} onChange={e => handleIntegrationChange(key, 'extraDate', e.target.value)} data-id="element-565" />
              </div>}
          </div>}
      </div>;
  };
  // --- Render ---
  const currentData = stepsData[step] || {
    consultantActions: [],
    clientActions: [],
    extraActivities: [],
    nonConformities: [],
    improvements: [],
    annotations: '',
    images: [],
    integrations: {}
  };
  const currentStepTitle = step >= 2 ? dynamicStepTitles[step - 2] : '';
  const isOperativeStep = step >= 2;
  // Reports: for standard mode, show from step 3 (Sopralluogo) onwards; for personalizzata, show on all steps
  const showReports = activityMode === 'personalizzata' ? step >= 2 : step >= 3;
  return <Modal isOpen={isOpen} onClose={handleClose} title={editActivity ? 'Modifica attività operativa' : 'Genera attività operativa'} size="2xl" data-id="element-566">
      <AnimatePresence mode="wait" data-id="element-567">
        {saveSuccess ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center py-16" data-id="element-568">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-569">
              <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-570" />
            </motion.div>
            <p className="text-lg font-semibold text-slate-800" data-id="element-571">
              {editActivity ? 'Attività operativa modificata con successo' : 'Attività operativa creata con successo'}
            </p>
          </motion.div> : step === 0 ?
      // STEP 0: TYPE CHOICE
      <motion.div key="type-choice" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-572">
            <div data-id="element-573">
              <h3 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4" data-id="element-574">
                Tipo di attività
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-575">
                <button onClick={() => setActivityMode('standard')} className={`p-5 rounded-xl border-2 text-left transition-all ${activityMode === 'standard' ? 'border-seform-orange bg-orange-50/50 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-576">
                  <div className="flex items-start gap-3" data-id="element-577">
                    <div className={`p-2.5 rounded-lg ${activityMode === 'standard' ? 'bg-seform-orange/10' : 'bg-slate-100'}`} data-id="element-578">
                      <ClipboardList className={`h-5 w-5 ${activityMode === 'standard' ? 'text-seform-orange' : 'text-slate-400'}`} data-id="element-579" />
                    </div>
                    <div data-id="element-580">
                      <h4 className={`font-semibold ${activityMode === 'standard' ? 'text-seform-navy' : 'text-slate-700'}`} data-id="element-581">
                        Attività standard
                      </h4>
                      <p className="text-sm text-slate-500 mt-1" data-id="element-582">
                        Flusso guidato con step predefiniti
                      </p>
                    </div>
                  </div>
                </button>
                <button onClick={() => setActivityMode('personalizzata')} className={`p-5 rounded-xl border-2 text-left transition-all ${activityMode === 'personalizzata' ? 'border-seform-orange bg-orange-50/50 shadow-sm' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-583">
                  <div className="flex items-start gap-3" data-id="element-584">
                    <div className={`p-2.5 rounded-lg ${activityMode === 'personalizzata' ? 'bg-seform-orange/10' : 'bg-slate-100'}`} data-id="element-585">
                      <Settings2 className={`h-5 w-5 ${activityMode === 'personalizzata' ? 'text-seform-orange' : 'text-slate-400'}`} data-id="element-586" />
                    </div>
                    <div data-id="element-587">
                      <h4 className={`font-semibold ${activityMode === 'personalizzata' ? 'text-seform-navy' : 'text-slate-700'}`} data-id="element-588">
                        Attività personalizzata
                      </h4>
                      <p className="text-sm text-slate-500 mt-1" data-id="element-589">
                        Configura il numero di attività contrattualizzate
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-590">
              <Button variant="flat" color="default" onClick={handleClose} data-id="element-591">
                Annulla
              </Button>
              <Button onClick={handleNextStep} endContent={<ChevronRight className="h-4 w-4" data-id="element-593" />} data-id="element-592">
                Avanti
              </Button>
            </div>
          </motion.div> : step === 1 ?
      // STEP 1: CONFIGURATION
      <motion.div key="config" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="space-y-6" data-id="element-594">
            <h3 className="text-lg font-bold text-seform-navy mb-4" data-id="element-595">
              Configurazione attività
            </h3>

            <div className="space-y-6 bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-596">
              <SearchableSelect label="Contratto" value={selectedContract} onChange={e => {
            const contractNum = e.target.value;
            setSelectedContract(contractNum);
            const contract = mockData.getContracts().find(c => c.number === contractNum);
            if (contract) {
              setSelectedClientName(contract.client);
            } else {
              setSelectedClientName('');
            }
          }} options={mockData.getContracts().map(c => ({
            value: c.number,
            label: `${c.number} - ${c.client}`
          }))} placeholder="Seleziona contratto..." data-id="element-597" />
              <div data-id="element-598">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-599">
                  Prodotto / Servizio
                </label>
                <div className="relative" data-id="element-600">
                  <input type="text" list="products-list" value={product} onChange={e => setProduct(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-seform-orange/50 focus:border-seform-orange transition-shadow" placeholder="Seleziona o scrivi prodotto..." data-id="element-601" />
                  <datalist id="products-list" data-id="element-602">
                    {productsList.map(p => <option key={p} value={p} data-id="element-603" />)}
                  </datalist>
                </div>
              </div>

              <div className="flex items-center gap-3" data-id="element-604">
                <input type="checkbox" id="repeatable" checked={isRepeatable} onChange={e => setIsRepeatable(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-seform-orange focus:ring-seform-orange" data-id="element-605" />
                <label htmlFor="repeatable" className="font-medium text-slate-700 cursor-pointer" data-id="element-606">
                  Attività ripetibile
                </label>
              </div>

              <AnimatePresence data-id="element-607">
                {isRepeatable && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="space-y-4 overflow-hidden" data-id="element-608">
                    <Input label="Numero di ripetizioni:" type="number" value={String(numRepetitions)} onChange={e => setNumRepetitions(Math.max(2, parseInt(e.target.value) || 2))} data-id="element-609" />
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex gap-3" data-id="element-610">
                      <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-611" />
                      <p className="text-sm text-blue-800" data-id="element-612">
                        Le attività saranno distribuite nel periodo del
                        contratto. Formula: n° attività / n° mesi durata
                        contratto. Data partenza: data accettazione contratto.
                        Date successive: calcolate dalla formula -15 giorni.
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="flex justify-between gap-3 pt-4 border-t border-slate-100" data-id="element-613">
              <Button variant="flat" color="default" onClick={handlePrevStep} startContent={<ChevronLeft className="h-4 w-4" data-id="element-615" />} data-id="element-614">
                Indietro
              </Button>
              <Button onClick={handleNextStep} disabled={!product} endContent={<ChevronRight className="h-4 w-4" data-id="element-617" />} data-id="element-616">
                Avanti
              </Button>
            </div>
          </motion.div> :
      // STEPS 2+: OPERATIVE STEPS
      <motion.div key={`step-${step}`} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="flex flex-col h-[80vh]" data-id="element-618">
            {/* Header Area (Sticky) */}
            <div className="flex-shrink-0 bg-white pb-4 border-b border-slate-100 z-10" data-id="element-619">
              <div className="flex justify-between items-end mb-4" data-id="element-620">
                {editActivity ? <div className="flex-1" data-id="element-621">
                    <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar" data-id="element-622">
                      {dynamicStepTitles.map((title, idx) => {
                  const stepNum = idx + 2;
                  const isActive = step === stepNum;
                  return <button key={stepNum} onClick={() => {
                    if (activityMode === 'personalizzata') {
                      setStepsData(prev => ({
                        ...prev,
                        [stepNum]: {
                          ...prev[stepNum],
                          integrativeDate: prev[stepNum]?.integrativeDate || getCurrentDateTime()
                        }
                      }));
                    }
                    setStep(stepNum);
                    resetForms();
                  }} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${isActive ? 'bg-seform-orange text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-623">
                            {idx + 1}.{' '}
                            {title.length > 20 ? title.substring(0, 20) + '...' : title}
                          </button>;
                })}
                    </div>
                  </div> : <div className="flex-1" data-id="element-624">
                    <div className="flex justify-between items-center mb-2" data-id="element-625">
                      <span className="text-sm font-bold text-seform-navy" data-id="element-626">
                        Step {step - 1} di {dynamicStepTitles.length}:{' '}
                        {currentStepTitle}
                      </span>
                      <span className="text-xs font-medium text-slate-500" data-id="element-627">
                        {Math.round((step - 1) / dynamicStepTitles.length * 100)}
                        % Completato
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-id="element-628">
                      <div className="bg-seform-orange h-2 rounded-full transition-all duration-300 ease-out" style={{
                  width: `${(step - 1) / dynamicStepTitles.length * 100}%`
                }} data-id="element-629"></div>
                    </div>
                  </div>}
              </div>

              <div className="flex justify-between items-center" data-id="element-630">
                <div className="flex items-center gap-3" data-id="element-631">
                  <Badge variant={activityStatus === 'Attiva' ? 'success' : 'warning'} data-id="element-632">
                    {activityStatus}
                  </Badge>
                  <Button size="sm" variant="flat" color={activityStatus === 'Attiva' ? 'warning' : 'success'} startContent={activityStatus === 'Attiva' ? <Pause className="h-3 w-3" data-id="element-634" /> : <Play className="h-3 w-3" data-id="element-635" />} onClick={() => setActivityStatus(s => s === 'Attiva' ? 'Sospesa' : 'Attiva')} data-id="element-633">
                    {activityStatus === 'Attiva' ? 'Sospendi' : 'Riattiva'}
                  </Button>
                </div>

                {showReports && <div className="flex gap-2" data-id="element-636">
                    <div className="relative group" data-id="element-637">
                      <Button size="sm" variant="flat" color="secondary" endContent={<ChevronDown className="h-3 w-3" data-id="element-639" />} data-id="element-638">
                        Genera report
                      </Button>
                      <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-lg shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50" data-id="element-640">
                        <button onClick={() => handleReportClick('pdf')} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 rounded-t-lg" data-id="element-641">
                          Crea PDF
                        </button>
                        <button onClick={() => handleReportClick('word')} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 rounded-b-lg" data-id="element-642">
                          Crea Word
                        </button>
                      </div>
                    </div>
                    <Button size="sm" variant="flat" color="secondary" startContent={<Mail className="h-4 w-4" data-id="element-644" />} onClick={() => handleReportClick('email')} data-id="element-643">
                      Invia report
                    </Button>
                  </div>}
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto py-6 pr-2 space-y-8 custom-scrollbar" data-id="element-645">
              {/* STEP SPECIFIC INTEGRATIONS */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-id="element-646">
                <h4 className="font-bold text-seform-navy mb-4 flex items-center gap-2" data-id="element-647">
                  <Settings2 className="h-4 w-4 text-seform-orange" data-id="element-648" /> Dettagli{' '}
                  {currentStepTitle}
                </h4>

                {step === 2 && activityMode === 'standard' &&
            // Calendarizzazione (standard only)
            <div className="space-y-2" data-id="element-649">
                    {renderIntegrationRow('cal_contatto', 'Contatto')}
                    {renderIntegrationRow('cal_calendarizzato', 'Calendarizzato')}
                  </div>}

                {(step === 3 || step === 5) && activityMode === 'standard' &&
            // Sopralluogo or Consegna (standard only)
            <div className="space-y-2" data-id="element-650">
                    {renderIntegrationRow('sop_fisico', 'Sopralluogo fisico', true, 'sop_type')}
                    {renderIntegrationRow('sop_online', 'Sopralluogo online', true, 'sop_type')}
                    {renderIntegrationRow('sop_distanza', 'Raccolta informazioni a distanza', true, 'sop_type')}
                  </div>}

                {step === 4 && activityMode === 'standard' &&
            // Back office (standard only)
            <div className="space-y-2" data-id="element-651">
                    {renderIntegrationRow('bo_verifica', 'Completamento e verifica informazioni e documenti')}
                    {renderIntegrationRow('bo_redazione', 'Redazione documentale')}
                    {renderIntegrationRow('bo_fascicolo', 'Aggiornamento (o creazione) fascicolo cliente')}
                    {renderIntegrationRow('bo_piano', 'Piano correttivo e di miglioramento')}
                    {renderIntegrationRow('bo_calendarizzazione', 'Calendarizzazione consegna')}
                  </div>}

                {(step >= 6 || activityMode === 'personalizzata') &&
            // Attività integrativa (standard step 6+) or ALL personalizzata steps
            <div className="space-y-4" data-id="element-652">
                    {/* Mandatory date for personalizzata steps */}
                    {activityMode === 'personalizzata' && <div data-id="element-653">
                        <Input label="Data dello step" type="datetime-local" value={currentData.integrativeDate || ''} onChange={e => setStepsData(prev => ({
                  ...prev,
                  [step]: {
                    ...prev[step],
                    integrativeDate: e.target.value
                  }
                }))} data-id="element-654" />
                      </div>}

                    <Input label="Situazione (breve descrizione)" value={currentData.integrativeDesc || ''} onChange={e => setStepsData(prev => ({
                ...prev,
                [step]: {
                  ...prev[step],
                  integrativeDesc: e.target.value
                }
              }))} data-id="element-655" />
                    <Select label="Tipo attività" options={customStepOptions.map(o => ({
                value: o,
                label: o
              }))} value={currentData.integrativeType || ''} onChange={e => setStepsData(prev => ({
                ...prev,
                [step]: {
                  ...prev[step],
                  integrativeType: e.target.value
                }
              }))} data-id="element-656" />
                    {currentData.integrativeType === 'Altro' && <Input label="Nome attività personalizzata" value={currentData.integrativeCustomName || ''} onChange={e => setStepsData(prev => ({
                ...prev,
                [step]: {
                  ...prev[step],
                  integrativeCustomName: e.target.value
                }
              }))} data-id="element-657" />}
                    {currentData.integrativeType && <div className="mt-4" data-id="element-658">
                        {renderIntegrationRow('int_detail', 'Dettagli esecuzione')}
                      </div>}
                  </div>}
              </div>

              {/* TEMPO ATTIVITÀ - Steps 3-6 (standard) or all personalizzata steps */}
              {(activityMode === 'standard' && step >= 3 && step <= 6 || activityMode === 'personalizzata') && <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-id="element-659">
                  <h4 className="font-bold text-seform-navy mb-4 flex items-center gap-2" data-id="element-660">
                    <Clock className="h-4 w-4 text-seform-orange" data-id="element-661" /> Tempo
                    attività
                  </h4>
                  <div className="flex flex-col gap-4" data-id="element-662">
                    <div className="flex items-end gap-3" data-id="element-663">
                      <div className="flex-1" data-id="element-664">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-665">
                          Aggiungi tempo (ore:minuti)
                        </label>
                        <div className="flex items-center gap-2" data-id="element-666">
                          <input type="number" min="0" max="99" placeholder="Ore" id={`time-hours-${step}`} className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-seform-orange/50 focus:border-seform-orange text-center" data-id="element-667" />
                          <span className="text-slate-400 font-bold" data-id="element-668">:</span>
                          <input type="number" min="0" max="59" placeholder="Min" id={`time-minutes-${step}`} className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-seform-orange/50 focus:border-seform-orange text-center" data-id="element-669" />
                          <Button size="sm" variant="flat" onClick={() => {
                      const hEl = document.getElementById(`time-hours-${step}`) as HTMLInputElement;
                      const mEl = document.getElementById(`time-minutes-${step}`) as HTMLInputElement;
                      const h = parseInt(hEl?.value || '0') || 0;
                      const m = parseInt(mEl?.value || '0') || 0;
                      if (h === 0 && m === 0) return;
                      setStepsData(prev => {
                        const sd = prev[step] || createEmptyStepData();
                        const entries = [...(sd.timeEntries || []), {
                          hours: h,
                          minutes: m
                        }];
                        const totalMin = entries.reduce((acc, e) => acc + e.hours * 60 + e.minutes, 0);
                        return {
                          ...prev,
                          [step]: {
                            ...sd,
                            timeEntries: entries,
                            totalTimeHours: Math.floor(totalMin / 60),
                            totalTimeMinutes: totalMin % 60
                          }
                        };
                      });
                      if (hEl) hEl.value = '';
                      if (mEl) mEl.value = '';
                    }} data-id="element-670">
                            <Plus className="h-4 w-4" data-id="element-671" /> Aggiungi
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Entries list */}
                    {(currentData.timeEntries || []).length > 0 && <div className="space-y-1" data-id="element-672">
                        {(currentData.timeEntries || []).map((entry, idx) => <div key={idx} className="flex items-center justify-between text-sm bg-slate-50 px-3 py-1.5 rounded-lg" data-id="element-673">
                            <span className="text-slate-600" data-id="element-674">
                              +{String(entry.hours).padStart(2, '0')}:
                              {String(entry.minutes).padStart(2, '0')}
                            </span>
                            <button onClick={() => {
                    setStepsData(prev => {
                      const sd = prev[step] || createEmptyStepData();
                      const entries = [...(sd.timeEntries || [])];
                      entries.splice(idx, 1);
                      const totalMin = entries.reduce((acc, e) => acc + e.hours * 60 + e.minutes, 0);
                      return {
                        ...prev,
                        [step]: {
                          ...sd,
                          timeEntries: entries,
                          totalTimeHours: Math.floor(totalMin / 60),
                          totalTimeMinutes: totalMin % 60
                        }
                      };
                    });
                  }} className="text-slate-400 hover:text-red-500 transition-colors" data-id="element-675">
                              <X className="h-3.5 w-3.5" data-id="element-676" />
                            </button>
                          </div>)}
                      </div>}

                    {/* Total */}
                    <div className="flex items-center justify-between bg-seform-orange/10 px-4 py-3 rounded-lg border border-seform-orange/20" data-id="element-677">
                      <span className="text-sm font-semibold text-seform-navy" data-id="element-678">
                        Totale tempo
                      </span>
                      <div className="flex items-center gap-2" data-id="element-679">
                        <input type="number" min="0" value={currentData.totalTimeHours || 0} onChange={e => {
                    const newH = parseInt(e.target.value) || 0;
                    setStepsData(prev => ({
                      ...prev,
                      [step]: {
                        ...prev[step],
                        totalTimeHours: newH
                      }
                    }));
                  }} className="w-14 px-2 py-1 border border-seform-orange/30 rounded text-sm text-center font-bold text-seform-navy bg-white" data-id="element-680" />
                        <span className="text-seform-navy font-bold" data-id="element-681">:</span>
                        <input type="number" min="0" max="59" value={currentData.totalTimeMinutes || 0} onChange={e => {
                    const newM = Math.min(59, parseInt(e.target.value) || 0);
                    setStepsData(prev => ({
                      ...prev,
                      [step]: {
                        ...prev[step],
                        totalTimeMinutes: newM
                      }
                    }));
                  }} className="w-14 px-2 py-1 border border-seform-orange/30 rounded text-sm text-center font-bold text-seform-navy bg-white" data-id="element-682" />
                        <span className="text-xs text-slate-500 ml-1" data-id="element-683">h:m</span>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* AZIONI (Consulente / Cliente) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-684">
                {/* Consulente */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col" data-id="element-685">
                  <h4 className="font-bold text-seform-navy mb-4 flex items-center gap-2" data-id="element-686">
                    <User className="h-4 w-4 text-seform-orange" data-id="element-687" /> Cosa fa il
                    consulente
                  </h4>
                  {renderActionList('consultant')}
                  {showAddConsultantAction ? <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3" data-id="element-688">
                      <Input label="Nome attività" value={consultantActionForm.name || ''} onChange={e => setConsultantActionForm({
                  ...consultantActionForm,
                  name: e.target.value
                })} required data-id="element-689" />
                      <Input label="Data e ora inizio" type="datetime-local" value={consultantActionForm.dateTime || getCurrentDateTime()} onChange={e => setConsultantActionForm({
                  ...consultantActionForm,
                  dateTime: e.target.value
                })} data-id="element-690" />
                      <Select label="Assegnatario" options={adminUsers} value={consultantActionForm.assignee || 'io'} onChange={e => setConsultantActionForm({
                  ...consultantActionForm,
                  assignee: e.target.value
                })} data-id="element-691" />
                      <Input label="Data e ora scadenza" type="datetime-local" value={consultantActionForm.deadline || ''} onChange={e => setConsultantActionForm({
                  ...consultantActionForm,
                  deadline: e.target.value
                })} data-id="element-692" />
                      <Textarea label="Annotazioni" value={consultantActionForm.notes || ''} onChange={e => setConsultantActionForm({
                  ...consultantActionForm,
                  notes: e.target.value
                })} data-id="element-693" />
                      <div className="flex justify-end gap-2 pt-2" data-id="element-694">
                        <Button size="sm" variant="flat" onClick={() => setShowAddConsultantAction(false)} data-id="element-695">
                          Annulla
                        </Button>
                        <Button size="sm" onClick={handleAddConsultantAction} disabled={!consultantActionForm.name} data-id="element-696">
                          Salva
                        </Button>
                      </div>
                    </div> : <Button size="sm" variant="flat" fullWidth onClick={() => setShowAddConsultantAction(true)} startContent={<Plus className="h-4 w-4" data-id="element-698" />} className="mt-auto" data-id="element-697">
                      Aggiungi azione
                    </Button>}
                </div>

                {/* Cliente */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col" data-id="element-699">
                  <h4 className="font-bold text-seform-navy mb-4 flex items-center gap-2" data-id="element-700">
                    <User className="h-4 w-4 text-blue-500" data-id="element-701" /> Cosa fa il
                    cliente
                  </h4>
                  {renderActionList('client')}
                  {showAddClientAction ? <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3" data-id="element-702">
                      <Input label="Nome attività" value={clientActionForm.name || ''} onChange={e => setClientActionForm({
                  ...clientActionForm,
                  name: e.target.value
                })} required data-id="element-703" />
                      <Input label="Data e ora inizio" type="datetime-local" value={clientActionForm.dateTime || getCurrentDateTime()} onChange={e => setClientActionForm({
                  ...clientActionForm,
                  dateTime: e.target.value
                })} data-id="element-704" />
                      <Input label="Assegnatario (Nome)" value={clientActionForm.assignee || ''} onChange={e => setClientActionForm({
                  ...clientActionForm,
                  assignee: e.target.value
                })} placeholder="Es. Mario Rossi" data-id="element-705" />
                      <Input label="Data e ora scadenza" type="datetime-local" value={clientActionForm.deadline || ''} onChange={e => setClientActionForm({
                  ...clientActionForm,
                  deadline: e.target.value
                })} data-id="element-706" />
                      <Textarea label="Annotazioni" value={clientActionForm.notes || ''} onChange={e => setClientActionForm({
                  ...clientActionForm,
                  notes: e.target.value
                })} data-id="element-707" />
                      <div className="flex justify-end gap-2 pt-2" data-id="element-708">
                        <Button size="sm" variant="flat" onClick={() => setShowAddClientAction(false)} data-id="element-709">
                          Annulla
                        </Button>
                        <Button size="sm" onClick={handleAddClientAction} disabled={!clientActionForm.name} data-id="element-710">
                          Salva
                        </Button>
                      </div>
                    </div> : <Button size="sm" variant="flat" fullWidth onClick={() => setShowAddClientAction(true)} startContent={<Plus className="h-4 w-4" data-id="element-712" />} className="mt-auto" data-id="element-711">
                      Aggiungi azione
                    </Button>}
                </div>
              </div>

              {/* ATTIVITA AGGIUNTIVE */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-id="element-713">
                <div className="flex justify-between items-center mb-4" data-id="element-714">
                  <h4 className="font-bold text-seform-navy flex items-center gap-2" data-id="element-715">
                    <ClipboardList className="h-4 w-4 text-seform-orange" data-id="element-716" />{' '}
                    Attività aggiuntive
                  </h4>
                  {!showAddExtraActivity && <Button size="sm" variant="flat" color="secondary" onClick={() => setShowAddExtraActivity(true)} startContent={<Plus className="h-4 w-4" data-id="element-718" />} data-id="element-717">
                      Aggiungi
                    </Button>}
                </div>
                <div className="space-y-3 mb-4" data-id="element-719">
                  {currentData.extraActivities.map(activity => <div key={activity.id} className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex justify-between items-start group" data-id="element-720">
                      <div data-id="element-721">
                        <div className="flex items-center gap-2 mb-1" data-id="element-722">
                          <span className="font-semibold text-sm text-slate-800" data-id="element-723">
                            {activity.type}
                          </span>
                          <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-100" data-id="element-724">
                            {activity.dateTime.replace('T', ' ')}
                          </span>
                        </div>
                        {activity.user && <p className="text-xs text-slate-600 flex items-center gap-1 mb-1" data-id="element-725">
                            <User className="h-3 w-3" data-id="element-726" />{' '}
                            {activity.user === 'io' ? 'Io' : activity.user}
                          </p>}
                        {activity.notes && <p className="text-sm text-slate-600 mt-2" data-id="element-727">
                            {activity.notes}
                          </p>}
                      </div>
                      <button onClick={() => deleteExtraActivity(activity.id)} className="p-2 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-md hover:bg-red-50" data-id="element-728">
                        <Trash2 className="h-4 w-4" data-id="element-729" />
                      </button>
                    </div>)}
                </div>
                {showAddExtraActivity && <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4" data-id="element-730">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-731">
                      <Select label="Tipo attività" options={extraActivityTypes} value={extraActivityForm.type || ''} onChange={e => setExtraActivityForm({
                  ...extraActivityForm,
                  type: e.target.value
                })} required data-id="element-732" />
                      <Input label="Data e ora" type="datetime-local" value={extraActivityForm.dateTime || getCurrentDateTime()} onChange={e => setExtraActivityForm({
                  ...extraActivityForm,
                  dateTime: e.target.value
                })} data-id="element-733" />
                      <Select label="Utente" options={adminUsers} value={extraActivityForm.user || 'io'} onChange={e => setExtraActivityForm({
                  ...extraActivityForm,
                  user: e.target.value
                })} data-id="element-734" />
                    </div>
                    <Textarea label="Annotazioni" value={extraActivityForm.notes || ''} onChange={e => setExtraActivityForm({
                ...extraActivityForm,
                notes: e.target.value
              })} data-id="element-735" />
                    <div className="flex justify-end gap-2 pt-2" data-id="element-736">
                      <Button variant="flat" onClick={() => setShowAddExtraActivity(false)} data-id="element-737">
                        Annulla
                      </Button>
                      <Button onClick={handleAddExtraActivity} disabled={!extraActivityForm.type} data-id="element-738">
                        Salva attività
                      </Button>
                    </div>
                  </div>}
              </div>

              {/* NC AND IMPROVEMENTS (Steps 3+) */}
              {showReports && <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-739">
                  {/* Non Conformità */}
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-id="element-740">
                    <div className="flex justify-between items-center mb-4" data-id="element-741">
                      <h4 className="font-bold text-seform-navy flex items-center gap-2" data-id="element-742">
                        <AlertTriangle className="h-4 w-4 text-red-500" data-id="element-743" /> Non
                        conformità rilevata
                      </h4>
                      <Button size="sm" variant="flat" color="danger" onClick={() => setShowNCModal(true)} startContent={<Plus className="h-4 w-4" data-id="element-745" />} data-id="element-744">
                        Aggiungi
                      </Button>
                    </div>
                    <div className="space-y-3" data-id="element-746">
                      {currentData.nonConformities.map(nc => <div key={nc.id} className="bg-red-50 p-3 rounded-lg border border-red-100" data-id="element-747">
                          <div className="flex justify-between items-start mb-1" data-id="element-748">
                            <span className="font-semibold text-sm text-red-800" data-id="element-749">
                              {nc.number}
                            </span>
                            <Badge variant="danger" size="sm" data-id="element-750">
                              {nc.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-red-700 mt-2" data-id="element-751">
                            {nc.description}
                          </p>
                        </div>)}
                      {currentData.nonConformities.length === 0 && <p className="text-sm text-slate-400 italic text-center py-2" data-id="element-752">
                          Nessuna non conformità inserita
                        </p>}
                    </div>
                  </div>

                  {/* Miglioramenti */}
                  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-id="element-753">
                    <div className="flex justify-between items-center mb-4" data-id="element-754">
                      <h4 className="font-bold text-seform-navy flex items-center gap-2" data-id="element-755">
                        <Lightbulb className="h-4 w-4 text-amber-500" data-id="element-756" />{' '}
                        Miglioramento da proporre
                      </h4>
                      <Button size="sm" variant="flat" color="warning" onClick={() => setShowImpModal(true)} startContent={<Plus className="h-4 w-4" data-id="element-758" />} data-id="element-757">
                        Aggiungi
                      </Button>
                    </div>
                    <div className="space-y-3" data-id="element-759">
                      {currentData.improvements.map(imp => <div key={imp.id} className="bg-amber-50 p-3 rounded-lg border border-amber-100" data-id="element-760">
                          <div className="flex justify-between items-start mb-1" data-id="element-761">
                            <span className="font-semibold text-sm text-amber-800" data-id="element-762">
                              {imp.number}
                            </span>
                            <span className="text-xs font-medium text-amber-600" data-id="element-763">
                              {imp.date}
                            </span>
                          </div>
                          <p className="font-medium text-sm text-amber-900" data-id="element-764">
                            {imp.subject}
                          </p>
                          <p className="text-sm text-amber-700 mt-1" data-id="element-765">
                            {imp.description}
                          </p>
                        </div>)}
                      {currentData.improvements.length === 0 && <p className="text-sm text-slate-400 italic text-center py-2" data-id="element-766">
                          Nessun miglioramento inserito
                        </p>}
                    </div>
                  </div>
                </div>}

              {/* ANNOTATIONS & IMAGES (All steps) */}
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-6" data-id="element-767">
                <div data-id="element-768">
                  <h4 className="font-bold text-seform-navy mb-3 flex items-center gap-2" data-id="element-769">
                    <FileText className="h-4 w-4 text-slate-400" data-id="element-770" /> Annotazioni
                  </h4>
                  <Textarea placeholder="Inserisci qui annotazioni generali per questo step..." value={currentData.annotations} onChange={e => setStepsData(prev => ({
                ...prev,
                [step]: {
                  ...prev[step],
                  annotations: e.target.value
                }
              }))} className="min-h-[100px]" data-id="element-771" />
                </div>

                <div data-id="element-772">
                  <h4 className="font-bold text-seform-navy mb-3 flex items-center gap-2" data-id="element-773">
                    <ImageIcon className="h-4 w-4 text-slate-400" data-id="element-774" /> Allegati e
                    Immagini
                  </h4>
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:bg-slate-50 cursor-pointer transition-colors group" data-id="element-775">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-white transition-colors" data-id="element-776">
                      <Upload className="h-6 w-6 text-slate-400 group-hover:text-seform-orange" data-id="element-777" />
                    </div>
                    <p className="text-sm text-slate-600 font-medium mb-1" data-id="element-778">
                      Trascina qui le immagini o clicca per caricare
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-779">
                      Supporta JPG, PNG, PDF (Max 10MB)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="flex-shrink-0 bg-white pt-4 border-t border-slate-100 mt-auto flex justify-between items-center z-10" data-id="element-780">
              {!(editActivity && step === 2) ? <Button variant="flat" color="default" onClick={handlePrevStep} startContent={<ChevronLeft className="h-4 w-4" data-id="element-782" />} data-id="element-781">
                  Indietro
                </Button> : <div data-id="element-783"></div>}
              <div className="flex gap-3" data-id="element-784">
                {/* Show "Aggiungi" for standard integrative steps (step >= 6) OR all personalizzata steps */}
                {(step >= 6 || activityMode === 'personalizzata') && <Button variant="flat" color="secondary" onClick={handleAddIntegrativeStep} startContent={<Plus className="h-4 w-4" data-id="element-786" />} data-id="element-785">
                    {activityMode === 'personalizzata' ? 'Aggiungi nuovo step' : 'Aggiungi attività integrativa'}
                  </Button>}
                <Button onClick={handleNextStep} disabled={activityMode === 'personalizzata' && currentData && !currentData.integrativeDate} endContent={step < dynamicStepTitles.length + 1 ? <ChevronRight className="h-4 w-4" data-id="element-788" /> : <Save className="h-4 w-4" data-id="element-789" />} data-id="element-787">
                  {step < dynamicStepTitles.length + 1 ? 'Avanti' : editActivity ? 'Salva modifiche' : 'Crea attività operativa'}
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* --- Sub-Modals --- */}

      {/* Report Content Dialog */}
      <Modal isOpen={showReportContentDialog} onClose={() => setShowReportContentDialog(false)} title="Seleziona contenuti del report" size="sm" data-id="element-790">
        <div className="space-y-4" data-id="element-791">
          <p className="text-sm text-slate-600" data-id="element-792">
            Seleziona quali liste di azioni includere nel report:
          </p>
          <div className="space-y-3" data-id="element-793">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-794">
              <input type="checkbox" id="rep-cons" checked={reportIncludeConsultant} onChange={e => setReportIncludeConsultant(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-seform-orange focus:ring-seform-orange" data-id="element-795" />
              <label htmlFor="rep-cons" className="font-medium text-sm text-slate-700 cursor-pointer" data-id="element-796">
                Cosa fa il consulente
              </label>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-797">
              <input type="checkbox" id="rep-cli" checked={reportIncludeClient} onChange={e => setReportIncludeClient(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-seform-orange focus:ring-seform-orange" data-id="element-798" />
              <label htmlFor="rep-cli" className="font-medium text-sm text-slate-700 cursor-pointer" data-id="element-799">
                Cosa fa il cliente
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4" data-id="element-800">
            <Button variant="flat" onClick={() => setShowReportContentDialog(false)} data-id="element-801">
              Annulla
            </Button>
            <Button onClick={confirmReportContent} disabled={!reportIncludeConsultant && !reportIncludeClient} data-id="element-802">
              Conferma
            </Button>
          </div>
        </div>
      </Modal>

      {/* Email Dialog */}
      <Modal isOpen={showEmailDialog} onClose={() => setShowEmailDialog(false)} title="Invia report via email" size="lg" data-id="element-803">
        <div className="space-y-4" data-id="element-804">
          <Input label="Destinatario" value={emailForm.to} onChange={e => setEmailForm({
          ...emailForm,
          to: e.target.value
        })} data-id="element-805" />
          <Input label="Oggetto" value={emailForm.subject} onChange={e => setEmailForm({
          ...emailForm,
          subject: e.target.value
        })} data-id="element-806" />
          <Textarea label="Corpo email" value={emailForm.body} onChange={e => setEmailForm({
          ...emailForm,
          body: e.target.value
        })} className="min-h-[150px]" placeholder="Scrivi un messaggio al cliente..." data-id="element-807" />
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 flex items-center gap-3" data-id="element-808">
            <FileText className="h-5 w-5 text-red-500" data-id="element-809" />
            <span className="text-sm font-medium text-slate-700" data-id="element-810">
              Report_Attivita.pdf
            </span>
            <span className="text-xs text-slate-400 ml-auto" data-id="element-811">
              Allegato generato automaticamente
            </span>
          </div>
          <div className="flex justify-end gap-2 pt-4" data-id="element-812">
            <Button variant="flat" onClick={() => setShowEmailDialog(false)} data-id="element-813">
              Annulla
            </Button>
            <Button onClick={sendEmail} startContent={<Mail className="h-4 w-4" data-id="element-815" />} data-id="element-814">
              Invia email
            </Button>
          </div>
        </div>
      </Modal>

      {/* NC Modal */}
      <Modal isOpen={showNCModal} onClose={() => setShowNCModal(false)} title="Nuova non conformità" size="2xl" data-id="element-816">
        <div className="space-y-6" data-id="element-817">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-818">
            <Select label="Tipologia" value={ncForm.type || ''} onChange={e => setNcForm({
            ...ncForm,
            type: e.target.value
          })} options={[{
            value: 'Non conformità maggiore',
            label: 'Non conformità maggiore'
          }, {
            value: 'Non conformità minore',
            label: 'Non conformità minore'
          }, {
            value: 'Osservazione',
            label: 'Osservazione'
          }]} required data-id="element-819" />
            <Select label="Origine" value={ncForm.origin || ''} onChange={e => setNcForm({
            ...ncForm,
            origin: e.target.value
          })} options={[{
            value: 'Interna',
            label: 'Interna'
          }, {
            value: 'Cliente (reclamo)',
            label: 'Cliente (reclamo)'
          }, {
            value: 'Fornitore',
            label: 'Fornitore'
          }, {
            value: 'Altro',
            label: 'Altro'
          }]} required data-id="element-820" />
            <DatePicker label="Data rilevazione" value={ncForm.date || ''} onChange={e => setNcForm({
            ...ncForm,
            date: e.target.value
          })} data-id="element-821" />
            <Select label="Responsabile" options={adminUsers} value={ncForm.responsible || 'io'} onChange={e => setNcForm({
            ...ncForm,
            responsible: e.target.value
          })} data-id="element-822" />
          </div>
          <Textarea label="Descrizione non conformità / Osservazione" value={ncForm.description || ''} onChange={e => setNcForm({
          ...ncForm,
          description: e.target.value
        })} required data-id="element-823" />
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100" data-id="element-824">
            <Button variant="flat" onClick={() => setShowNCModal(false)} data-id="element-825">
              Annulla
            </Button>
            <Button onClick={handleSaveNC} disabled={!ncForm.description} data-id="element-826">
              Salva
            </Button>
          </div>
        </div>
      </Modal>

      {/* Improvement Modal */}
      <Modal isOpen={showImpModal} onClose={() => setShowImpModal(false)} title="Nuovo miglioramento" size="2xl" data-id="element-827">
        <div className="space-y-6" data-id="element-828">
          <Input label="Oggetto" value={impForm.subject || ''} onChange={e => setImpForm({
          ...impForm,
          subject: e.target.value
        })} required data-id="element-829" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-830">
            <DatePicker label="Data proposta" value={impForm.date || ''} onChange={e => setImpForm({
            ...impForm,
            date: e.target.value
          })} data-id="element-831" />
            <Select label="Responsabile" options={adminUsers} value={impForm.responsible || 'io'} onChange={e => setImpForm({
            ...impForm,
            responsible: e.target.value
          })} data-id="element-832" />
          </div>
          <Textarea label="Descrizione miglioramento" value={impForm.description || ''} onChange={e => setImpForm({
          ...impForm,
          description: e.target.value
        })} required data-id="element-833" />
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100" data-id="element-834">
            <Button variant="flat" onClick={() => setShowImpModal(false)} data-id="element-835">
              Annulla
            </Button>
            <Button onClick={handleSaveImp} disabled={!impForm.subject || !impForm.description} data-id="element-836">
              Salva
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>;
}